import React from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  Tooltip,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import designTokens, {
  colors,
  layerZIndex,
  numbers,
  typography,
  shadows,
} from "../../styles/designTokens";
import { keyframes } from "@mui/material/styles";
import type { UploadStatus } from "../../types";

const globalUploadTooltipSlotProps = {
  popper: {
    sx: { zIndex: layerZIndex.globalUploadTooltip },
  },
};

const GLOBAL_UPLOAD_PROGRESS_TRACK = "rgba(255, 255, 255, 0.10)";
const GLOBAL_UPLOAD_PANEL_BG = "#2A2A2D";
const GLOBAL_UPLOAD_STATUS_GREY = "#BDBDBF";
const GLOBAL_UPLOAD_STATUS_FAILED = "#E21919";
const GLOBAL_UPLOAD_STATUS_WARNING =
  designTokens.semantic.libraryDetailDrawer.conversionCheck.warning;
const GLOBAL_UPLOAD_STATUS_ATTENTION = "#FFE435";
const GLOBAL_UPLOAD_PROGRESS_GREY = "#78787C";
const GLOBAL_UPLOAD_PROGRESS_FAILED_TRACK = "#3A3A3E";
const GLOBAL_UPLOAD_PROGRESS_ACTIVE = "rgba(80, 183, 134, 0.20)";
const GLOBAL_UPLOAD_HEADER_HEIGHT = 48;
const GLOBAL_UPLOAD_ROW_HEIGHT = 82;
const GLOBAL_UPLOAD_PROGRESS_STEP_COUNT = 5;
const CAPACITY_LIMIT_STATUS_TEXT = "Import failed. Capacity limit reached.";

export const activeProgressSweep = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;
export const ACTIVE_PROGRESS_SWEEP_ANIMATION = `${activeProgressSweep} 1.2s ease-in-out infinite`;

export const COMPLETION_FILL_DURATION_MS = 500;
export const completionProgressFill = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;
export const COMPLETION_PROGRESS_FILL_ANIMATION = `${completionProgressFill} ${COMPLETION_FILL_DURATION_MS}ms ease-in-out forwards`;

type ProgressSegmentState = "complete" | "active" | "empty";

export interface UploadToastFile {
  id?: string;
  fileName?: string;
  statusText?: string;
  status?: UploadStatus;
  progress?: number;
  paused?: boolean;
  clickActionReady?: boolean;
  detailActionLabel?: string;
  underlineStatusAction?: boolean;
  /** 경고 Import는 완료 상태를 유지한 채 상태 문구만 앰버로 바뀐다. */
  statusTone?: "default" | "warning";
  staticProgress?: boolean;
  onFileClick?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
}

interface FileUploadToastProps {
  open?: boolean;
  files?: UploadToastFile[];
  defaultMinimized?: boolean;
  onClose?: () => void;
  sx?: SxProps<Theme>;
  minimized?: boolean;
  onToggleMinimize?: () => void;
}

const HeaderChevronIcon: React.FC<{ minimized: boolean }> = ({ minimized }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 24, height: 24, display: "block" }}
    aria-hidden
  >
    <path
      d={minimized ? "M7 14L12 9L17 14" : "M7 10L12 15L17 10"}
      fill="none"
      stroke="#BDBDBF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const HeaderCloseIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 24, height: 24, display: "block" }}
    aria-hidden
  >
    <path
      d="M7 7L17 17M17 7L7 17"
      fill="none"
      stroke="#BDBDBF"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Box>
);

const CancelCircleIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 24, height: 24, display: "block" }}
    aria-hidden
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="none"
      stroke="#FF4242"
      strokeWidth="2"
    />
    <path
      d="M9 9L15 15M15 9L9 15"
      fill="none"
      stroke="#FF4242"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Box>
);

const CompleteCircleIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 24, height: 24, display: "block" }}
    aria-hidden
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="none"
      stroke="#50B786"
      strokeWidth="2"
    />
    <path
      d="M8 12.5L10.8 15L16 9.5"
      fill="none"
      stroke="#50B786"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const AttentionCircleIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 24, height: 24, display: "block" }}
    aria-hidden
  >
    <circle
      cx="12"
      cy="12"
      r="8.5"
      fill="none"
      stroke={GLOBAL_UPLOAD_STATUS_ATTENTION}
      strokeWidth="2"
    />
    <path
      d="M12 7.5V12.5"
      fill="none"
      stroke={GLOBAL_UPLOAD_STATUS_ATTENTION}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16" r="1.2" fill={GLOBAL_UPLOAD_STATUS_ATTENTION} />
  </Box>
);

const FailedCircleIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 24, height: 24, display: "block" }}
    aria-hidden
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="rgba(255, 66, 66, 0.12)"
      stroke="#FF4242"
      strokeWidth="2"
    />
    <path
      d="M9 9L15 15M15 9L9 15"
      fill="none"
      stroke="#FF4242"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Box>
);

const isCapacityLimitStatus = (
  status?: UploadStatus,
  statusText?: string,
): boolean =>
  status === "failed_capacity_post_conversion" ||
  (statusText || "") === CAPACITY_LIMIT_STATUS_TEXT;

const isSuccessStatus = (status?: UploadStatus, statusText?: string): boolean =>
  !isCapacityLimitStatus(status, statusText) &&
  (status === "completed" ||
    (statusText || "") === "Complete" ||
    (statusText || "") === "Object added.");

const isFailureStatus = (status?: UploadStatus): boolean =>
  status === "failed" || status === "cancelled";

const getActiveProgressStep = (file: UploadToastFile): number => {
  switch (file.status) {
    case "uploading":
    case "uploaded":
      return 0;
    case "converting":
      return 1;
    case "optimizing":
      return 2;
    case "processing":
    case "bundling":
    case "rendering":
    case "finalizing":
      return 3;
    default:
      break;
  }

  const progress = Math.max(0, Math.min(100, file.progress || 0));
  if (progress >= 80) return 3;
  if (progress >= 60) return 3;
  if (progress >= 40) return 2;
  if (progress >= 20) return 1;
  return 0;
};

const getFileKey = (file: UploadToastFile): string =>
  file.id || file.fileName || file.statusText || "upload";

const getPrefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(
    getPrefersReducedMotion,
  );

  React.useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

interface AnimatedSegmentedProgressBarProps {
  file: UploadToastFile;
  displayProgress: number;
  isComplete: boolean;
  onTargetActivated: () => void;
}

const AnimatedSegmentedProgressBar: React.FC<
  AnimatedSegmentedProgressBarProps
> = ({ file, displayProgress, isComplete, onTargetActivated }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const targetStep =
    Boolean(file.staticProgress) || isComplete
      ? GLOBAL_UPLOAD_PROGRESS_STEP_COUNT
      : getActiveProgressStep(file);
  const [visualStep, setVisualStep] = React.useState(targetStep);
  const [completingStep, setCompletingStep] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    if (prefersReducedMotion || file.staticProgress) {
      setCompletingStep(null);
      setVisualStep(targetStep);
    }
  }, [file.staticProgress, prefersReducedMotion, targetStep]);

  React.useEffect(() => {
    if (
      prefersReducedMotion ||
      file.staticProgress ||
      completingStep !== null ||
      visualStep >= targetStep
    ) {
      return;
    }

    setCompletingStep(visualStep);
  }, [
    completingStep,
    file.staticProgress,
    prefersReducedMotion,
    targetStep,
    visualStep,
  ]);

  React.useEffect(() => {
    if (completingStep === null) return undefined;

    const timeoutId = window.setTimeout(() => {
      setVisualStep(completingStep + 1);
      setCompletingStep(null);
    }, COMPLETION_FILL_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [completingStep]);

  React.useEffect(() => {
    if (completingStep === null && visualStep === targetStep) {
      onTargetActivated();
    }
  }, [completingStep, onTargetActivated, targetStep, visualStep]);

  const visualProgress = visualStep * 20;
  const accessibleProgress =
    completingStep !== null || visualStep < targetStep
      ? Math.min(displayProgress, visualProgress)
      : Math.max(displayProgress, visualProgress);

  return (
    <Box
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={accessibleProgress}
      data-progress-variant="segmented"
      sx={{
        display: "flex",
        gap: "2px",
        height: 8,
        width: "100%",
      }}
    >
      {Array.from({ length: GLOBAL_UPLOAD_PROGRESS_STEP_COUNT }).map(
        (_, index) => {
          const state: ProgressSegmentState =
            index < visualStep
              ? "complete"
              : index === visualStep
                ? "active"
                : "empty";
          const isCompleting = completingStep === index;

          return (
            <Box
              key={index}
              data-testid={`progress-segment-${index + 1}`}
              data-progress-segment={index + 1}
              data-progress-state={isCompleting ? "completing" : state}
              sx={{
                position: "relative",
                flex: "1 1 0",
                minWidth: 0,
                height: "100%",
                overflow: "hidden",
                backgroundColor: GLOBAL_UPLOAD_PROGRESS_TRACK,
                borderTopLeftRadius: index === 0 ? numbers.cornerRadius[40] : 0,
                borderBottomLeftRadius:
                  index === 0 ? numbers.cornerRadius[40] : 0,
                borderTopRightRadius:
                  index === GLOBAL_UPLOAD_PROGRESS_STEP_COUNT - 1
                    ? numbers.cornerRadius[40]
                    : 0,
                borderBottomRightRadius:
                  index === GLOBAL_UPLOAD_PROGRESS_STEP_COUNT - 1
                    ? numbers.cornerRadius[40]
                    : 0,
              }}
            >
              {state === "complete" && !isCompleting && (
                <Box
                  data-progress-fill="complete"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: colors.primary[50].dark,
                  }}
                />
              )}
              {isCompleting && (
                <Box
                  data-testid="progress-fill-completion"
                  data-progress-fill="completion"
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: 0,
                    backgroundColor: colors.primary[50].dark,
                    animation: COMPLETION_PROGRESS_FILL_ANIMATION,
                    willChange: "width",
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                      width: "100%",
                      willChange: "auto",
                    },
                  }}
                />
              )}
              {state === "active" && !isCompleting && (
                <Box
                  data-testid="progress-fill-sweep"
                  data-progress-fill="sweep"
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: 0,
                    backgroundColor: GLOBAL_UPLOAD_PROGRESS_ACTIVE,
                    animation: ACTIVE_PROGRESS_SWEEP_ANIMATION,
                    willChange: "width",
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                      width: "50%",
                      willChange: "auto",
                    },
                  }}
                />
              )}
            </Box>
          );
        },
      )}
    </Box>
  );
};

const FileUploadToast: React.FC<FileUploadToastProps> = ({
  open = true,
  files = [],
  defaultMinimized = false,
  onClose = () => {},
  sx = {},
  minimized: minimizedProp,
  onToggleMinimize,
}) => {
  const [internalMinimized, setInternalMinimized] =
    React.useState(!!defaultMinimized);
  const minimized =
    minimizedProp !== undefined ? minimizedProp : internalMinimized;
  const presentedFilesRef = React.useRef(new Map<string, UploadToastFile>());
  const [, forcePresentationUpdate] = React.useReducer(
    (version: number) => version + 1,
    0,
  );

  files.forEach((file) => {
    const statusLabel = file.statusText || "Uploading";
    const isImmediatePresentation =
      Boolean(file.staticProgress) ||
      isCapacityLimitStatus(file.status, statusLabel) ||
      isFailureStatus(file.status) ||
      statusLabel.toLowerCase().includes("failed");
    const fileKey = getFileKey(file);
    if (!presentedFilesRef.current.has(fileKey) || isImmediatePresentation) {
      presentedFilesRef.current.set(fileKey, file);
    }
  });

  React.useEffect(() => {
    const visibleFileKeys = new Set(files.map(getFileKey));
    presentedFilesRef.current.forEach((_, fileKey) => {
      if (!visibleFileKeys.has(fileKey)) {
        presentedFilesRef.current.delete(fileKey);
      }
    });
  }, [files]);

  const presentFile = React.useCallback((file: UploadToastFile) => {
    const fileKey = getFileKey(file);
    if (presentedFilesRef.current.get(fileKey) === file) return;
    presentedFilesRef.current.set(fileKey, file);
    forcePresentationUpdate();
  }, []);

  const handleToggleMin = () => {
    if (onToggleMinimize) {
      onToggleMinimize();
      return;
    }
    setInternalMinimized((prev) => !prev);
  };

  if (!open || files.length === 0) return null;

  const getStatusColor = (file: UploadToastFile): string => {
    const statusLabel = file.statusText || "Uploading";
    const lowered = statusLabel.toLowerCase();
    if (isFailureStatus(file.status) || lowered.includes("failed")) {
      return GLOBAL_UPLOAD_STATUS_FAILED;
    }
    if (file.statusTone === "warning") return GLOBAL_UPLOAD_STATUS_WARNING;
    return GLOBAL_UPLOAD_STATUS_GREY;
  };

  const getDisplayProgress = (file: UploadToastFile): number => {
    const statusLabel = file.statusText || "";
    if (isCapacityLimitStatus(file.status, statusLabel)) return 100;
    if (
      isFailureStatus(file.status) ||
      statusLabel.toLowerCase().includes("failed")
    ) {
      return 0;
    }
    if (isSuccessStatus(file.status, statusLabel)) return 100;
    return Math.max(0, Math.min(100, file.progress || 0));
  };

  const renderProgressBar = (file: UploadToastFile): React.ReactNode => {
    const statusLabel = file.statusText || "Uploading";
    const lowered = statusLabel.toLowerCase();
    const displayProgress = getDisplayProgress(file);
    const isCapacityLimit = isCapacityLimitStatus(file.status, statusLabel);
    const isFailed = isFailureStatus(file.status) || lowered.includes("failed");

    if (isCapacityLimit || isFailed) {
      return (
        <Box
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={displayProgress}
          data-progress-variant={isCapacityLimit ? "capacity" : "failed"}
          sx={{
            height: 8,
            width: "100%",
            borderRadius: numbers.cornerRadius[40],
            backgroundColor: isCapacityLimit
              ? GLOBAL_UPLOAD_PROGRESS_GREY
              : GLOBAL_UPLOAD_PROGRESS_FAILED_TRACK,
          }}
        />
      );
    }

    const isComplete =
      Boolean(file.staticProgress) || isSuccessStatus(file.status, statusLabel);

    return (
      <AnimatedSegmentedProgressBar
        file={file}
        displayProgress={displayProgress}
        isComplete={isComplete}
        onTargetActivated={() => presentFile(file)}
      />
    );
  };

  const renderAction = (file: UploadToastFile): React.ReactNode => {
    const statusLabel = file.statusText || "Uploading";
    const isCapacityLimit = isCapacityLimitStatus(file.status, statusLabel);
    const isCompleted = isSuccessStatus(file.status, statusLabel);
    const isFailed =
      isFailureStatus(file.status) ||
      statusLabel.toLowerCase().includes("failed");

    if (isCapacityLimit) {
      return (
        <Box role="img" aria-label="Capacity limit status">
          <AttentionCircleIcon />
        </Box>
      );
    }
    if (isCompleted) {
      return (
        <Box role="img" aria-label="Complete status">
          <CompleteCircleIcon />
        </Box>
      );
    }
    if (isFailed) {
      return (
        <Box role="img" aria-label="Upload failed status">
          <FailedCircleIcon />
        </Box>
      );
    }

    if (!file.onCancel) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Tooltip title="Cancel" slotProps={globalUploadTooltipSlotProps}>
          <IconButton
            size="small"
            aria-label="Cancel upload"
            onClick={file.onCancel}
            sx={cancelIconBtnSx}
            disableRipple
          >
            <CancelCircleIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderFileRow = (file: UploadToastFile): React.ReactNode => {
    const fileKey = getFileKey(file);
    const presentationFile = presentedFilesRef.current.get(fileKey) || file;
    const statusLabel = presentationFile.statusText || "Uploading";
    const isFileClickable = Boolean(
      presentationFile.clickActionReady && presentationFile.onFileClick,
    );
    const isRowClickable =
      isFileClickable && !presentationFile.detailActionLabel;

    return (
      <Box
        key={fileKey}
        role={isRowClickable ? "button" : undefined}
        aria-label={
          isRowClickable
            ? presentationFile.fileName || "Open import details"
            : undefined
        }
        tabIndex={isRowClickable ? 0 : undefined}
        onClick={isRowClickable ? presentationFile.onFileClick : undefined}
        onKeyDown={
          isRowClickable
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  presentationFile.onFileClick?.();
                }
              }
            : undefined
        }
        sx={{
          height: GLOBAL_UPLOAD_ROW_HEIGHT,
          px: numbers.gap[24],
          py: numbers.gap[16],
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flexShrink: 0,
          cursor: isRowClickable ? "pointer" : "default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: numbers.gap[12],
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              component="p"
              sx={{
                ...typography.styles.label2Medium,
                color: colors.common[100].dark,
                mb: "2px",
                display: "block",
                width: "100%",
                textAlign: "left",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "inherit",
                textDecoration: "none",
                "&:hover": isRowClickable
                  ? {
                      color: colors.primary[50].dark,
                    }
                  : undefined,
              }}
              noWrap
            >
              {presentationFile.fileName || "File name.step"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                ...typography.styles.caption,
                color: getStatusColor(presentationFile),
                lineHeight: "14px",
                textDecoration: presentationFile.underlineStatusAction
                  ? "underline"
                  : "none",
                textUnderlineOffset: presentationFile.underlineStatusAction
                  ? "2px"
                  : undefined,
              }}
            >
              {statusLabel}
            </Typography>
          </Box>
          {renderAction(presentationFile)}
          {isFileClickable && presentationFile.detailActionLabel && (
            <ButtonBase
              onClick={(event) => {
                event.stopPropagation();
                presentationFile.onFileClick?.();
              }}
              disableRipple
              sx={{
                ...typography.styles.caption,
                color: colors.primary[50].dark,
                whiteSpace: "nowrap",
                px: numbers.gap[8],
              }}
            >
              {presentationFile.detailActionLabel}
            </ButtonBase>
          )}
        </Box>

        {renderProgressBar(file)}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: 331,
        height: minimized
          ? GLOBAL_UPLOAD_HEADER_HEIGHT
          : GLOBAL_UPLOAD_HEADER_HEIGHT +
            files.length * GLOBAL_UPLOAD_ROW_HEIGHT,
        background: GLOBAL_UPLOAD_PANEL_BG,
        color: colors.common[100].dark,
        borderTopLeftRadius: numbers.cornerRadius[12],
        borderTopRightRadius: numbers.cornerRadius[12],
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        boxShadow: shadows.fileUploadToast,
        overflow: "hidden",
        pointerEvents: "auto",
        ...sx,
      }}
    >
      <Box
        sx={{
          height: GLOBAL_UPLOAD_HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: numbers.gap[24],
          pr: numbers.gap[16],
          py: numbers.gap[12],
          borderBottom: minimized
            ? "none"
            : `1px solid ${colors.grey[40].dark}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.grey[80].dark,
            ...typography.styles.caption,
          }}
        >
          Import
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <Tooltip
            title={minimized ? "Expand" : "Minimize"}
            slotProps={globalUploadTooltipSlotProps}
          >
            <IconButton
              size="small"
              onClick={handleToggleMin}
              sx={headerIconBtnSx}
              disableRipple
            >
              <HeaderChevronIcon minimized={minimized} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close" slotProps={globalUploadTooltipSlotProps}>
            <IconButton
              size="small"
              onClick={onClose}
              sx={headerIconBtnSx}
              disableRipple
            >
              <HeaderCloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {!minimized && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: files.length * GLOBAL_UPLOAD_ROW_HEIGHT,
          }}
        >
          {files.map(renderFileRow)}
        </Box>
      )}
    </Box>
  );
};

const headerIconBtnSx = {
  color: colors.grey[80].dark,
  padding: 0,
  width: "24px",
  height: "24px",
  minWidth: "24px",
  minHeight: "24px",
  borderRadius: "999px",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

const cancelIconBtnSx = {
  padding: 0,
  minWidth: "24px",
  minHeight: "24px",
  width: "24px",
  height: "24px",
  color: colors.red[50].dark,
  backgroundColor: "transparent",
  borderRadius: "50%",
  border: "none",
  boxSizing: "border-box",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

export default FileUploadToast;
